import React from 'react';
import omit from 'lodash-es/omit';
import { WithRouterProps } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import { useMediaQuery } from 'ha/helpers/MediaQuery';
import ListingCard from 'ha/components/next/ListingCard';
import { normalizeDate } from '@ha/date';
import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';

import { getMapViewState } from '../selectors';
import { setVisitedRoom } from '../actions';

export const EVENT_NAME = 'Search listing clicked';

interface Props {
  listingId: number;
  pageInfo: { offset: number; limit: number };
  cityCanonical: string;
  countryCode: string;
  filtersAnalytics: {
    listingIdResults: number[];
    numberOfOrganicListings: number;
    numberOfPartnerListings: number;
    numberOfSearchResults: number;
  };
  listingsAnalytics: {
    listingIdResults: number[];
  };
  trackLocation?: string;
  track: (name: string, params: object) => void;
  isAdvertiserSearch: boolean;
  room: NormalizedAlgoliaListing;
  onMouseEnter?: (id: number) => void;
  onMouseLeave?: (id: number) => void;
  isLazy?: boolean;
  scrollableElement?: HTMLElement | 'window';
  bottomOffset?: string;
  withGallery?: boolean;
  withFavorites?: boolean;
  isMapMarker?: boolean;
  isMobile: boolean;
}

const ListingPreviewCardTracked: React.FC<Props & WithRouterProps> = ({
  track,
  cityCanonical,
  countryCode,
  filtersAnalytics,
  pageInfo: { offset, limit },
  listingId,
  listingsAnalytics: { listingIdResults },
  isAdvertiserSearch,
  trackLocation,
  location: { query },
  isMobile,
  ...listingProps
}) => {
  const dispatch = useDispatch();
  const { md } = useMediaQuery();

  const mapViewCurrentState: string = useSelector(getMapViewState);

  const handleCardClick = React.useCallback(() => {
    const params = {
      type: 'link',
      cityCanonical,
      countryCode,
      ...omit(filtersAnalytics, [
        'listingIdResults',
        'numberOfOrganicListings',
        'numberOfPartnerListings',
        'numberOfSearchResults',
      ]),
      position: offset + listingIdResults.indexOf(listingId) + 1,
      page: Math.floor(offset / limit) + 1,
      pageType: isAdvertiserSearch ? 'advertiser-search' : 'search',
      cardLocation: trackLocation,
      isAdvancedPricing: listingProps.room.priceType === 'monthly',
      ...(md && { mapType: mapViewCurrentState }),
    };

    track(EVENT_NAME, params);

    dispatch(setVisitedRoom(listingId));
  }, [
    cityCanonical,
    countryCode,
    filtersAnalytics,
    offset,
    listingIdResults,
    listingId,
    limit,
    isAdvertiserSearch,
    trackLocation,
    listingProps.room.priceType,
    md,
    mapViewCurrentState,
    track,
    dispatch,
  ]);

  const queryParams = React.useMemo(
    () => ({
      ...query,
      startDate: normalizeDate(query.startDate) || null,
      endDate: normalizeDate(query.endDate) || null,
    }),
    [query],
  );

  const props = {
    ...listingProps,
    queryParams,
    onCardClick: handleCardClick,
    cardLocation: trackLocation,
  };

  return (
    <ListingCard {...props} linkTarget={isMobile ? undefined : '_blank'} />
  );
};

export default ListingPreviewCardTracked;
